import { getCompanyLegacyId, getToken } from ".";
import { HttpException } from "../../models/exceptions";
import { MailTemplatePath } from "../../models/mail-template";
import store from "../../store";

// ### Fetching mail templates for a certain path: event/action/target
export async function fetchMailTemplates({
  signal,
  mailTemplatePath,
  legacyId,
}: {
  signal: any;
  mailTemplatePath: MailTemplatePath;
  legacyId?: string;
}) {
  const companyLegacyId = legacyId ?? (await getCompanyLegacyId());
  const token = await getToken();
  const { language, event, action, target, version } = mailTemplatePath;

  let url = `${
    (window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST
  }/api/2/customers/${companyLegacyId}/mail-templates/language/${language}/event/${event}/action/${action}/target/${target}`;

  if (version) {
    url += `/version/${version}`;
  }

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let templates = await response.json();
  templates = templates.map((t: any) => ({
    ...t,
    language,
    event,
    action,
    target,
  }));

  return templates;
}

// ### Creating a new mail template on a certain path: event/action/target/version
export async function postMailTemplate({
  mailTemplatePath,
  subject,
  body,
}: {
  mailTemplatePath: MailTemplatePath;
  subject: any;
  body: any;
}) {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();
  const { language, event, action, target, version } = mailTemplatePath;

  if (version === "live") {
    const error = new Error("You cannot directly save a live version. Please promote a draft.");
    throw error;
  }

  const url = `${
    (window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST
  }/api/2/customers/${companyLegacyId}/mail-templates/language/${language}/event/${event}/action/${action}/target/${target}/version/${version}`;

  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify({
      subject: subject,
      html: body,
      language,
      event,
      action,
      target,
      version,
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while storing the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return { subject: subject, body: body };
}

// ### Copying the mailTemplate for a certain path from the template customer: event/action/target
export async function copyFromTemplateCustomerToDraft({ mailTemplatePath }: { mailTemplatePath: MailTemplatePath }) {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();
  const { language, event, action, target } = mailTemplatePath;

  const url = `${
    (window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST
  }/api/2/customers/${companyLegacyId}/mail-templates/language/${language}/event/${event}/action/${action}/target/${target}/copy-from-template`;

  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify({
      language,
      event,
      action,
      target,
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const data = await response.json();
    const error = new HttpException("An error occurred while trying to copy from template");
    error.code = response.status;
    error.info = data["message"];
    throw error;
  }

  return await response.json();
}

// ### Deleting a mail template on a certain path: event/action/target/version
export async function deleteMailTemplate({ mailTemplatePath }: { mailTemplatePath: MailTemplatePath }) {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();
  const { language, event, action, target, version } = mailTemplatePath;

  const url = `${
    (window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST
  }/api/2/customers/${companyLegacyId}/mail-templates/language/${language}/event/${event}/action/${action}/target/${target}/version/${version}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while deleting the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return true;
}

// ### Promoting a mail template on a certain path to the live version: event/action/target/version
export async function promoteMailTemplate({ mailTemplatePath }: { mailTemplatePath: MailTemplatePath }) {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();
  const { language, event, action, target, version } = mailTemplatePath;

  if (version === "live") {
    const error = new Error("You cannot promote a live version.");
    throw error;
  }

  const url = `${
    (window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST
  }/api/2/customers/${companyLegacyId}/mail-templates/language/${language}/event/${event}/action/${action}/target/${target}/version/${version}/promote`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while promoting the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return true;
}

// ### Rendering a mail template to its final HTML (no tracking, no test decoration)
export async function renderMailTemplate({
  signal,
  mailTemplatePath,
  fields = {},
  translationStatus = "approved",
}: {
  signal?: any;
  mailTemplatePath: MailTemplatePath;
  fields?: any;
  // Which translation-key values the t("key") calls in this template resolve against.
  translationStatus?: "draft" | "approved";
}) {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();
  const { language, event, action, target, version } = mailTemplatePath;

  const url = `${
    (window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST
  }/api/2/customers/${companyLegacyId}/mail-templates/language/${language}/event/${event}/action/${action}/target/${target}/version/${version}/render`;

  const response = await fetch(url, {
    signal: signal,
    method: "POST",
    // test_by_watchtower forces every conditional block to render, so the preview shows
    // the whole template instead of only the branches these fields happen to hit.
    body: JSON.stringify({
      fields,
      test_by_watchtower: true,
      use_draft_translations: translationStatus === "draft",
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while rendering the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return (await response.json()) as { subject: string; html: string };
}

// ### Sending test mails
export async function testMailTemplate(payload: {
  languages: string[];
  fields: any;
  paths: MailTemplatePath[];
  live: boolean;
  draft: boolean;
  active: boolean;
}) {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/mail-templates/send-test-bulk`;

  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while promoting the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return true;
}

// ### Copying replacement blocks from the template customer
export async function copyReplacementBlocks() {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  const url = `${
    (window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST
  }/api/2/customers/${companyLegacyId}/mail-templates/copy-replacement-blocks`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while copying replacement blocks.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return await response.json();
}

// ### Getting on overview on all drafts
export async function fetchMailTemplateSummary({ signal, legacyId }: { signal: any; legacyId?: string }) {
  const companyLegacyId = legacyId ?? (await getCompanyLegacyId());
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/mail-templates/summary`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the draft count.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const summary = await response.json();

  return summary;
}

export async function fetchMailTemplateChangeHistory({ signal }: { signal: any }) {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/mail-templates/change-history`;
  if (store.getState().company.mailTemplateChangeHistoryInclDrafts) {
    url += "?includeDrafts=true";
  }

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let changeHistory = await response.json();

  return changeHistory;
}

export async function fetchMailTemplateSends({ signal, legacyId }: { signal: any; legacyId?: string }) {
  const companyLegacyId = legacyId ?? (await getCompanyLegacyId());
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/mail-templates/sends`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let sends = await response.json();

  return sends;
}
